import React, { useEffect, useState, useSyncExternalStore } from 'react';
import { useConnectivity } from '../../hooks/useConnectivity';
import './offlineRibbon.scss';

// How long "Back online" stays up after a recovery.
const RECOVERED_FLASH_MS = 3_000;
// Matches .offline-ribbon's height in offlineRibbon.scss.
const RIBBON_HEIGHT_PX = 26;

const MESSAGES = {
  offline: "Offline — changes can't be saved until you reconnect",
  unreachable: "Can't reach the server — changes can't be saved until it's back",
  recovered: 'Back online',
} as const;

// Inline SVG, not Font Awesome: the icon font comes from a CDN, so on a screen
// that hadn't drawn an icon before the connection dropped it can't load —
// exactly when this ribbon appears. Paths from Lucide (ISC licence):
// wifi-off (iOS's `wifi.slash`), triangle-alert, circle-check.
const ICON_PATHS: Record<keyof typeof MESSAGES, React.ReactNode> = {
  offline: (
    <>
      <path d="M12 20h.01" />
      <path d="M8.5 16.429a5 5 0 0 1 7 0" />
      <path d="M5 12.859a10 10 0 0 1 5.17-2.69" />
      <path d="M19 12.859a10 10 0 0 0-2.007-1.523" />
      <path d="M2 8.82a15 15 0 0 1 4.177-2.643" />
      <path d="M22 8.82a15 15 0 0 0-11.288-3.764" />
      <path d="m2 2 20 20" />
    </>
  ),
  unreachable: (
    <>
      <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3" />
      <path d="M12 9v4" />
      <path d="M12 17h.01" />
    </>
  ),
  recovered: (
    <>
      <circle cx="12" cy="12" r="10" />
      <path d="m9 12 2 2 4-4" />
    </>
  ),
};

// The app chrome (Layout / SystemAdminLayout) mounts an inline ribbon under its
// header, which pushes the page down rather than covering it — as on iOS. Every
// other screen (login, PIN, workspace picker) gets the floating one mounted in
// App. Counting the inline ones lets the floating one stand down while the
// chrome has it covered, so the two never show at once.
let inlineMounted = 0;
const inlineListeners = new Set<() => void>();
const subscribeInline = (l: () => void) => {
  inlineListeners.add(l);
  return () => { inlineListeners.delete(l); };
};
const getInlineMounted = () => inlineMounted;
const setInlineMounted = (delta: number) => {
  inlineMounted += delta;
  inlineListeners.forEach(l => l());
};

type Props = {
  /** `inline` sits in the layout's flow; `floating` overlays the top of the viewport. */
  placement: 'inline' | 'floating';
};

/**
 * Slim ribbon shown while the app can't reach the API, matching the iOS
 * OfflineBanner. Two lost states with their own wording (no network at all vs
 * the server not answering), and a brief "Back online" when it recovers.
 */
const OfflineRibbon: React.FC<Props> = ({ placement }) => {
  const { status, recoveredAt } = useConnectivity();
  const inlineCount = useSyncExternalStore(subscribeInline, getInlineMounted);

  useEffect(() => {
    if (placement !== 'inline') return;
    setInlineMounted(1);
    return () => setInlineMounted(-1);
  }, [placement]);

  // Re-render when the "Back online" flash should end.
  const [, setTick] = useState(0);
  const flashRemaining = recoveredAt ? recoveredAt + RECOVERED_FLASH_MS - Date.now() : 0;
  useEffect(() => {
    if (status !== 'online' || flashRemaining <= 0) return;
    const t = setTimeout(() => setTick(n => n + 1), flashRemaining);
    return () => clearTimeout(t);
  }, [status, flashRemaining]);

  const kind: keyof typeof MESSAGES | null =
    placement === 'floating' && inlineCount > 0 ? null
      : status !== 'online' ? status
      : flashRemaining > 0 ? 'recovered'
      : null;

  // While the floating ribbon covers the top of the page, tell fixed-position
  // corner controls (the pickers' Sign out) to sit below it.
  const floatingShown = placement === 'floating' && !!kind;
  useEffect(() => {
    if (!floatingShown) return;
    const root = document.documentElement;
    root.style.setProperty('--offline-ribbon-offset', `${RIBBON_HEIGHT_PX}px`);
    return () => { root.style.removeProperty('--offline-ribbon-offset'); };
  }, [floatingShown]);

  if (!kind) return null;

  return (
    <div
      className={`offline-ribbon offline-ribbon--${placement} offline-ribbon--${kind}`}
      role={kind === 'recovered' ? 'status' : 'alert'}
    >
      <svg
        className="offline-ribbon__icon"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={2.5}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        {ICON_PATHS[kind]}
      </svg>
      <span>{MESSAGES[kind]}</span>
    </div>
  );
};

export default OfflineRibbon;
